/**
 * WorkflowSummary.jsx
 *
 * Header for the WorkflowPane — shows step count, validation summary,
 * workflow description, submit/cancel buttons, and post-completion
 * summary generation with export capabilities.
 */

import { useState, useEffect } from 'react';
import {
    Box, Typography, Chip, Button, CircularProgress, IconButton, TextField, Tooltip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { UserActions } from 'BMapsCmds';
import { DialogSubmitButton, DialogCancelButton } from '../../common/DialogActionButtons';
import { WorkflowState, StepStatus } from './constants';
import { titleFromPlan } from './campaignTitle';
import { CampaignRefusalRecord } from '../../CampaignProgress';
import { generateSummary } from '../../../gibbs/workflow/summaryGenerator';
import {
    toMarkdown, toHTML, toPlainText, downloadFile, copyToClipboard,
} from '../../../gibbs/workflow/summaryExport';

const stateLabels = {
    [WorkflowState.PROPOSED]: 'Proposed',
    [WorkflowState.EDITING]: 'Editing',
    [WorkflowState.VALIDATED]: 'Ready',
    [WorkflowState.SUBMITTED]: 'Running',
    [WorkflowState.COMPLETE]: 'Complete',
};

const stateColors = {
    [WorkflowState.PROPOSED]: 'info',
    [WorkflowState.EDITING]: 'warning',
    [WorkflowState.VALIDATED]: 'success',
    [WorkflowState.SUBMITTED]: 'primary',
    [WorkflowState.COMPLETE]: 'success',
};

function validationCounts(stepValidation) {
    const values = Object.values(stepValidation);
    return {
        valid: values.filter((s) => s === StepStatus.VALID).length,
        warning: values.filter((s) => s === StepStatus.WARNING).length,
        invalid: values.filter((s) => s === StepStatus.INVALID).length,
        total: values.length,
    };
}

/**
 * Build metadata object for export functions from provenance summary and plan.
 *
 * `title` is the name the panel leads with, including a reviewer's rename. An
 * export that says "Workflow: <the whole summary paragraph>" names the run by
 * the text the header stopped showing, so the display name wins where there is
 * one and `plan.summary` remains the fallback.
 */
function buildExportMetadata(provenanceSummary, plan, title) {
    return {
        date: new Date().toISOString(),
        workflowName: title || plan?.summary || 'Workflow',
        duration: (provenanceSummary?.totalTime || 0) / 1000,
        stepCount: (plan?.actions || []).length,
        stepsCompleted: provenanceSummary?.completed || 0,
        stepsFailed: provenanceSummary?.failed || 0,
    };
}

/**
 * Short, human display name for a workflow: the title before an em/en-dash or
 * colon separator in the plan summary, capped at 5 words. Keeps the header
 * scannable instead of showing the full multi-sentence description.
 */
function deriveWorkflowName(plan) {
    const raw = (plan?.name || plan?.summary || 'Workflow').trim();
    const head = (raw.split(/\s+[—–]\s+|\s+-\s+|:\s+/)[0] || raw).trim() || raw;
    const words = head.split(/\s+/);
    return words.length <= 5 ? head : `${words.slice(0, 5).join(' ')}…`;
}

/**
 * Render a single summary section with an h3 header.
 */
function SummarySection({ title, content }) {
    if (!content) return null;
    return (
        <Box sx={{ mb: 1.5 }}>
            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.5 }}>
                {title}
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {content}
            </Typography>
        </Box>
    );
}

/**
 * The name the panel leads with, and the control that renames it.
 *
 * Her 08-03 ask: the summary paragraph took about a third of the panel, and
 * "what people are really gonna care about is the steps that are below it". The
 * paragraph is still what the narrative and the refusal record are built on, so
 * it is not discarded — it moves to the title's tooltip, where it costs nothing.
 */
function WorkflowTitle({ title, summary, onRename }) {
    const [draft, setDraft] = useState(null);
    const editing = draft !== null;

    const commit = () => {
        const next = (draft || '').trim();
        // An emptied field is a cancelled rename, not a nameless campaign.
        if (next && next !== title) onRename(next);
        setDraft(null);
    };

    if (editing) {
        return (
            <TextField
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                onBlur={commit}
                onKeyDown={(event) => {
                    if (event.key === 'Enter') commit();
                    if (event.key === 'Escape') setDraft(null);
                }}
                size="small"
                variant="standard"
                autoFocus
                slotProps={{ htmlInput: { 'aria-label': 'Campaign name' } }}
                data-testid="workflow-title-input"
                sx={{ flexGrow: 1, mr: 1 }}
            />
        );
    }

    return (
        <Box sx={{
            display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0,
        }}
        >
            <Tooltip title={summary || ''} placement="bottom-start">
                <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    noWrap
                    data-testid="workflow-title"
                >
                    {title}
                </Typography>
            </Tooltip>
            {onRename && (
                <IconButton
                    size="small"
                    onClick={() => setDraft(title)}
                    aria-label="Rename this campaign"
                    data-testid="workflow-title-rename"
                >
                    <EditIcon fontSize="inherit" />
                </IconButton>
            )}
        </Box>
    );
}

export default function WorkflowSummary({
    plan, state, stepValidation, onSubmit, onCancel, onCancelWorkflow,
    provenanceSummary, provenanceLog, workflow, workspace,
    submitLabel = 'Submit', submitBlockedReason = null,
    refusalRecord = '', formatMarkdown = (text) => text,
    title = '', onTitleChange = null,
}) {
    const actions = plan?.actions || [];
    // The reviewer's rename when there is one, otherwise derived from the plan.
    // Named once here so the header and the summary export cannot disagree.
    const displayTitle = title || titleFromPlan(plan);
    const counts = validationCounts(stepValidation);
    const allValid = counts.total > 0 && counts.invalid === 0;
    const canSubmit = state === WorkflowState.VALIDATED
        || (state === WorkflowState.EDITING && allValid)
        || state === WorkflowState.PROPOSED;

    // Summary generation state
    const [summary, setSummary] = useState(null);
    const [generating, setGenerating] = useState(false);
    const [copyFeedback, setCopyFeedback] = useState('');

    const handleGenerateSummary = async () => {
        setGenerating(true);
        const result = await generateSummary(provenanceLog, workflow, workspace);
        setSummary(result);
        setGenerating(false);
    };

    const handleCopyToClipboard = async () => {
        if (!summary) return;
        const metadata = buildExportMetadata(provenanceSummary, plan, displayTitle);
        const text = toPlainText(summary, metadata);
        const ok = await copyToClipboard(text);
        setCopyFeedback(ok ? 'Copied!' : 'Copy failed');
        setTimeout(() => setCopyFeedback(''), 2000);
    };

    const handleDownloadMarkdown = () => {
        if (!summary) return;
        const metadata = buildExportMetadata(provenanceSummary, plan, displayTitle);
        const md = toMarkdown(summary, metadata);
        downloadFile(md, 'workflow-summary.md', 'text/markdown');
    };

    const handleDownloadHTML = () => {
        if (!summary) return;
        const metadata = buildExportMetadata(provenanceSummary, plan, displayTitle);
        const html = toHTML(summary, metadata);
        downloadFile(html, 'workflow-summary.html', 'text/html');
    };

    // Show Generate Summary button when complete and provenance has steps
    const canGenerateSummary = state === WorkflowState.COMPLETE
        && provenanceLog
        && provenanceLog.stepCount > 0;

    return (
        <Box sx={{ p: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
            {/* Title row */}
            <Box sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5,
            }}
            >
                <WorkflowTitle
                    title={displayTitle}
                    summary={plan?.summary || ''}
                    onRename={onTitleChange}
                />
                <Chip
                    label={stateLabels[state] || state}
                    color={stateColors[state] || 'default'}
                    size="small"
                />
            </Box>

            {/* Step count + validation summary */}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {actions.length}
                {' '}
                step
                {actions.length !== 1 ? 's' : ''}
                {counts.total > 0 && (
                    <>
                        {' \u2014 '}
                        {counts.valid}
                        {' '}
                        valid
                        {counts.warning > 0 && `, ${counts.warning} warning`}
                        {counts.invalid > 0 && `, ${counts.invalid} invalid`}
                    </>
                )}
            </Typography>

            {/* Action buttons */}
            {state !== WorkflowState.SUBMITTED && state !== WorkflowState.COMPLETE && (
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <DialogSubmitButton
                        onClick={onSubmit}
                        disabled={!canSubmit || Boolean(submitBlockedReason)}
                        size="small"
                        // The shared button keeps its filled primary look when
                        // disabled, so a blocked campaign read as a live button
                        // that simply did nothing when clicked. Dim it here
                        // rather than restyling the shared component.
                        sx={(!canSubmit || submitBlockedReason)
                            ? { opacity: 0.45 }
                            : undefined}
                    >
                        {submitLabel}
                    </DialogSubmitButton>
                    <DialogCancelButton
                        onClick={onCancel}
                        size="small"
                    >
                        Cancel
                    </DialogCancelButton>
                </Box>
            )}
            {state !== WorkflowState.SUBMITTED
                && state !== WorkflowState.COMPLETE
                && submitBlockedReason && (
                <Typography
                    variant="caption"
                    color="warning.main"
                    sx={{ display: 'block', mt: 0.75 }}
                    data-testid="workflow-submit-blocked-reason"
                >
                    {submitBlockedReason}
                </Typography>
            )}
            {/*
              * A refused campaign now leaves an artifact. Her 07-28 ask was that
              * error messages be exportable too, and her bar for any campaign
              * document is whether the attempt could be reconstructed from it —
              * which a reason printed on screen and nowhere else cannot meet.
              */}
            {state !== WorkflowState.SUBMITTED
                && state !== WorkflowState.COMPLETE
                && submitBlockedReason && (
                <CampaignRefusalRecord
                    record={refusalRecord}
                    formatMarkdown={formatMarkdown}
                />
            )}

            {/* Cancel Workflow button — visible during execution */}
            {state === WorkflowState.SUBMITTED && (
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <DialogCancelButton
                        onClick={onCancelWorkflow}
                        size="small"
                    >
                        Cancel Workflow
                    </DialogCancelButton>
                </Box>
            )}

            {/* Completion summary (6.4) */}
            {state === WorkflowState.COMPLETE && provenanceSummary && (
                <Box sx={{
                    mt: 1, p: 1, bgcolor: 'action.hover', borderRadius: 1,
                }}
                >
                    <Typography variant="body2" fontWeight="bold" sx={{ mb: 0.5 }}>
                        Workflow Complete
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {provenanceSummary.completed}
                        {' '}
                        completed
                        {provenanceSummary.failed > 0 && `, ${provenanceSummary.failed} failed`}
                        {provenanceSummary.skipped > 0 && `, ${provenanceSummary.skipped} skipped`}
                        {provenanceSummary.totalTime > 0 && ` — ${(provenanceSummary.totalTime / 1000).toFixed(1)}s`}
                    </Typography>
                    {/* Results live in their own pane; surface them from the
                        completed workflow so the user can always get back to
                        the ranked compounds + energies for this run. */}
                    <Box sx={{ mt: 1 }}>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={() => UserActions.OpenSidePanel('right', { pageId: 'results' })}
                            data-testid="view-results-btn"
                        >
                            View Results
                        </Button>
                    </Box>
                </Box>
            )}

            {/* Generate Summary button + loading state */}
            {canGenerateSummary && !generating && (
                <Box sx={{ mt: 1 }}>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={handleGenerateSummary}
                        data-testid="generate-summary-btn"
                    >
                        Generate Summary
                    </Button>
                </Box>
            )}

            {generating && (
                <Box sx={{
                    mt: 1, display: 'flex', alignItems: 'center', gap: 1,
                }}
                >
                    <CircularProgress size={16} />
                    <Typography variant="body2" color="text.secondary">
                        Generating summary...
                    </Typography>
                </Box>
            )}

            {/* Generated summary display */}
            {summary && !generating && (
                <Box
                    sx={{
                        mt: 1,
                        p: 1.5,
                        bgcolor: 'background.paper',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        maxHeight: 300,
                        overflowY: 'auto',
                    }}
                    data-testid="summary-panel"
                >
                    {summary.error && (
                        <Typography variant="caption" color="warning.main" sx={{ display: 'block', mb: 1 }}>
                            Note:
                            {' '}
                            {summary.error}
                            {' '}
                            (showing fallback summary)
                        </Typography>
                    )}
                    <SummarySection title="Objective" content={summary.objective} />
                    <SummarySection title="Methods" content={summary.methods} />
                    <SummarySection title="Results" content={summary.results} />
                    <SummarySection title="Key Findings" content={summary.keyFindings} />

                    {/* Export buttons */}
                    <Box sx={{
                        mt: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap',
                    }}
                    >
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={handleCopyToClipboard}
                            data-testid="copy-clipboard-btn"
                        >
                            {copyFeedback || 'Copy to Clipboard'}
                        </Button>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={handleDownloadMarkdown}
                            data-testid="download-md-btn"
                        >
                            Download Markdown
                        </Button>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={handleDownloadHTML}
                            data-testid="download-html-btn"
                        >
                            Download HTML
                        </Button>
                    </Box>
                </Box>
            )}
        </Box>
    );
}
